import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, IdCard, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPES = [
  { id: "single_guy", label: "Single Man" },
  { id: "single_lady", label: "Single Lady" },
  { id: "couple", label: "Couple" },
  { id: "tv", label: "TV" },
];

const fmtGBP = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n || 0);

export default function MembershipPublic() {
  const { slug } = useParams();
  const [offering, setOffering] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [type, setType] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [buyer, setBuyer] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    dob: "",
    fabswingers_username: "",
    swinghub_profile: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/venues/${slug}/membership`);
        setOffering(data);
        const firstAvailable = TYPES.find((t) => Number(data.pricing?.[t.id]) > 0);
        if (firstAvailable) setType(firstAvailable.id);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "This venue doesn't sell memberships");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const canSubmit =
    type &&
    buyer.first_name.trim() &&
    buyer.last_name.trim() &&
    /.+@.+\..+/.test(buyer.email) &&
    buyer.phone.trim().length >= 5 &&
    buyer.dob &&
    (buyer.fabswingers_username.trim() || buyer.swinghub_profile.trim()) &&
    termsAccepted &&
    !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/checkout/membership/create`, {
        venue_slug: slug,
        membership_type: type,
        origin_url: window.location.origin,
        buyer,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-[#F5F5F0] flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!offering) {
    return (
      <div className="min-h-screen bg-black text-[#F5F5F0] flex items-center justify-center p-6 text-center">
        <div>
          <IdCard size={40} className="mx-auto mb-4 text-[#D9A6F5]" />
          <h1 className="text-2xl font-black">Memberships not available</h1>
          <p className="mt-2 text-sm text-[#A3A3A3]">This venue doesn&apos;t sell digital memberships.</p>
        </div>
      </div>
    );
  }

  const availableTypes = TYPES.filter((t) => Number(offering.pricing?.[t.id]) > 0);

  return (
    <div className="min-h-screen bg-black text-[#F5F5F0] py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          {offering.venue_logo && (
            <img
              src={offering.venue_logo}
              alt=""
              className="h-14 w-auto mx-auto mb-4 object-contain"
            />
          )}
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D9A6F5] mb-2">
            Digital Membership
          </div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tighter">
            Join {offering.venue_name}
          </h1>
          <p className="mt-3 text-sm text-[#A3A3A3]">
            {offering.duration_type === "lifetime"
              ? "Lifetime membership · one payment"
              : offering.duration_type === "custom_months"
              ? `${offering.duration_months}-month membership`
              : "1-year membership · renew manually each year"}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#A3A3A3] mb-3">
              Membership type
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {availableTypes.map((t) => (
                <button
                  type="button"
                  key={t.id}
                  onClick={() => setType(t.id)}
                  data-testid={`mem-type-${t.id}`}
                  className={`p-3 border text-left transition-colors ${
                    type === t.id
                      ? "border-[#D9A6F5] bg-[#D9A6F5]/10"
                      : "border-white/15 hover:border-white/30"
                  }`}
                >
                  <div className="text-xs uppercase tracking-[0.18em] text-[#F5F5F0]">
                    {t.label}
                  </div>
                  <div className="text-xl font-black mt-1">
                    {fmtGBP(offering.pricing[t.id])}
                  </div>
                </button>
              ))}
            </div>
            {availableTypes.length === 0 && (
              <div className="border border-white/10 p-6 text-center text-sm text-[#A3A3A3]">
                No membership tiers are currently available. Please contact the venue.
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input label="First name" value={buyer.first_name} onChange={(v) => setBuyer({ ...buyer, first_name: v })} testId="mem-first-name" />
            <Input label="Last name" value={buyer.last_name} onChange={(v) => setBuyer({ ...buyer, last_name: v })} testId="mem-last-name" />
            <Input label="Email" type="email" value={buyer.email} onChange={(v) => setBuyer({ ...buyer, email: v })} testId="mem-email" />
            <Input label="Phone" type="tel" value={buyer.phone} onChange={(v) => setBuyer({ ...buyer, phone: v })} testId="mem-phone" />
            <Input label="Date of birth" type="date" value={buyer.dob} onChange={(v) => setBuyer({ ...buyer, dob: v })} testId="mem-dob" />
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#A3A3A3] mb-1">
              Verify with a swinger profile
            </div>
            <p className="text-[11px] text-[#A3A3A3]/80 mb-3">
              Provide either a FabSwingers username or SwingHub profile link (helps venues verify you).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="FabSwingers username" value={buyer.fabswingers_username} onChange={(v) => setBuyer({ ...buyer, fabswingers_username: v })} testId="mem-fs" />
              <Input label="SwingHub profile URL" value={buyer.swinghub_profile} onChange={(v) => setBuyer({ ...buyer, swinghub_profile: v })} testId="mem-sh" />
            </div>
          </div>

          {type && (
            <div className="border border-white/10 bg-white/5 p-4 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-[#A3A3A3]">Membership</span>
                <span>{fmtGBP(offering.pricing[type])}</span>
              </div>
              {!offering.hide_booking_fees && (
                <div className="flex justify-between">
                  <span className="text-[#A3A3A3]">Booking fee</span>
                  <span>{fmtGBP(1.95)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 mt-2 border-t border-white/10 font-bold">
                <span>Total</span>
                <span data-testid="mem-total" className="text-xl">
                  {fmtGBP(Number(offering.pricing[type]) + (offering.hide_booking_fees ? 0 : 1.95))}
                </span>
              </div>
            </div>
          )}

          <label
            className="flex items-start gap-3 border border-white/10 hover:border-[#D9A6F5]/40 p-3.5 cursor-pointer transition-colors"
            data-testid="mem-terms-wrap"
          >
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              data-testid="mem-terms-checkbox"
              className="mt-1 h-4 w-4 accent-[#D9A6F5] flex-shrink-0"
              required
            />
            <span className="text-[12px] text-[#A3A3A3] leading-relaxed">
              I have read and agree to the{" "}
              <a
                href="/terms/tickets"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mem-terms-link"
                className="text-[#D9A6F5] hover:underline font-bold"
              >
                Ticket Purchase Terms &amp; Conditions
              </a>
              .
            </span>
          </label>

          <button
            type="submit"
            disabled={!canSubmit || !offering.ready}
            data-testid="mem-buy-btn"
            className="w-full bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-50 py-4 text-sm uppercase tracking-widest font-bold inline-flex items-center justify-center gap-2 transition-colors"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {busy ? "Redirecting to Stripe…" : "Buy membership"}
          </button>
          {!offering.ready && (
            <p className="text-center text-[11px] text-amber-400">
              This venue isn&apos;t currently accepting payments online.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3]">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1 w-full bg-black border border-white/15 h-11 px-3 text-[#F5F5F0] text-sm focus:outline-none focus:border-[#D9A6F5]"
      />
    </label>
  );
}
